// Pure helpers for Meta's Australian regional-regulation fields (verified advertiser).
// No I/O. Shared by the publish mapper and scripts/probe-meta-regional-identity.ts.
//
// Proven live 2026-10-23 (probe handback): sending
//   regionalRegulatedCategories: ['AUSTRALIA_UNIVERSAL']
//   regionalRegulationIdentities: { universal_beneficiary: <id>, universal_payer: <id> }
// fixed subcode 3858634. Every ID goes as its exact digit STRING (Zernio, 2026-10-29); the
// earlier number form for short IDs is retired because strings are accepted for all lengths.

export const AU_CATEGORY = 'AUSTRALIA_UNIVERSAL';

export const VERIFIED_ADVERTISER_ID_MESSAGE =
  'Must be the numeric Verified identity ID from Meta Business Settings > Verified identities (not a Page or Business ID).';

// null = valid-and-empty (clear); string = normalised digits; Error text = invalid.
export function parseVerifiedAdvertiserId(
  raw: string | null | undefined,
): { ok: true; value: string | null } | { ok: false; message: string } {
  const v = (raw ?? '').trim();
  if (v === '') return { ok: true, value: null };
  if (!/^\d{5,20}$/.test(v)) {
    return { ok: false, message: VERIFIED_ADVERTISER_ID_MESSAGE };
  }
  return { ok: true, value: v };
}

export function isAuCode(code: unknown): boolean {
  return typeof code === 'string' && code.trim().toUpperCase() === 'AU';
}

export type GeoCounts = {
  countries: number;
  cities: number;
  regions: number;
  places: number;
  neighborhoods: number;
  zips: number;
  customLocations: number;
};

const EMPTY_COUNTS = (): GeoCounts => ({
  countries: 0, cities: 0, regions: 0, places: 0, neighborhoods: 0, zips: 0, customLocations: 0,
});

// Geo keys in both shapes: TargetingConfig / Zernio camelCase and Meta snake_case.
const GEO_KEYS: Array<[keyof GeoCounts, string[]]> = [
  ['countries', ['countries']],
  ['cities', ['cities']],
  ['regions', ['regions']],
  ['places', ['places']],
  ['neighborhoods', ['neighborhoods']],
  ['zips', ['zips']],
  ['customLocations', ['customLocations', 'custom_locations']],
];

function entryIsAu(entry: unknown): boolean {
  if (isAuCode(entry)) return true;
  if (!entry || typeof entry !== 'object') return false;
  const e = entry as Record<string, unknown>;
  return isAuCode(e.country_code) || isAuCode(e.countryCode) || isAuCode(e.country);
}

export type GeoEvidence = { counts: GeoCounts; au: boolean; total: number };

// Geo present in one object, read from the object itself AND from a nested
// geo_locations / geoLocations container. INCLUDED geo only (excluded_* ignored).
export function geoFrom(obj: unknown): GeoEvidence {
  const counts = EMPTY_COUNTS();
  let au = false;
  if (obj && typeof obj === 'object') {
    const root = obj as Record<string, unknown>;
    const containers: Array<Record<string, unknown>> = [root];
    for (const k of ['geo_locations', 'geoLocations']) {
      const c = root[k];
      if (c && typeof c === 'object') containers.push(c as Record<string, unknown>);
    }
    for (const c of containers) {
      for (const [field, keys] of GEO_KEYS) {
        for (const k of keys) {
          const v = c[k];
          if (!Array.isArray(v)) continue;
          counts[field] += v.length;
          if (v.some(entryIsAu)) au = true;
        }
      }
    }
  }
  const total = Object.values(counts).reduce((x, y) => x + y, 0);
  return { counts, au, total };
}

export function describeGeo(c: GeoCounts): string {
  return (Object.keys(c) as Array<keyof GeoCounts>).map((k) => `${k}=${c[k]}`).join(', ');
}

export function isAustraliaCountry(country: string | null | undefined): boolean {
  return (country ?? '').trim().toLowerCase() === 'australia';
}

// The Australia rule: an explicit AU code on any geo item in any source wins; otherwise the
// account country must be Australia AND at least one geo item must exist.
export function evidenceTargetsAu(evidence: GeoEvidence[], accountCountry: string | null | undefined): boolean {
  if (evidence.some((e) => e.au)) return true;
  return isAustraliaCountry(accountCountry) && evidence.some((e) => e.total > 0);
}

// Does this ad set target Australia? Reads the TargetingConfig fields, its rawPayload
// (extracted mode, snake or camel) and, when given, the BUILT body (what Meta will receive).
export function targetsAustralia(
  targeting: (Record<string, unknown> & { rawPayload?: unknown }) | null | undefined,
  accountCountry: string | null | undefined,
  builtBody?: Record<string, unknown> | null,
): boolean {
  const evidence: GeoEvidence[] = [geoFrom(targeting), geoFrom(targeting?.rawPayload)];
  if (builtBody) evidence.push(geoFrom(builtBody), geoFrom(builtBody.rawTargeting));
  return evidenceTargetsAu(evidence, accountCountry);
}

// Did this built body carry the verified-advertiser identity? (Used to word the error.)
export function regionalIdentitySent(body: Record<string, unknown> | null | undefined): boolean {
  return !!body && typeof body === 'object' && body.regionalRegulationIdentities != null;
}

// The wire value for a validated digit string. ALWAYS the exact digit STRING (2026-10-29):
// the upgraded SDK types regionalRegulationIdentities as string | number and Zernio's note says
// to send each ID as a string of digits, passed to Meta digit for digit. Number() is never used.
export function identityWireValue(id: string): string {
  return id;
}

// {} or the two body fields. Callers validate the ID with parseVerifiedAdvertiserId.
export function buildRegionalRegulationFields(
  identityId: string | null | undefined,
  targetsAu: boolean,
): Record<string, unknown> {
  const id = (identityId ?? '').trim();
  if (id === '' || !targetsAu) return {};
  if (!/^\d+$/.test(id)) return {};
  const n = identityWireValue(id);
  return {
    regionalRegulatedCategories: [AU_CATEGORY],
    regionalRegulationIdentities: { universal_beneficiary: n, universal_payer: n },
  };
}
